import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { organizationService } from '../services/organizationService';
import { userService } from '../services/userService';
import { integrationService } from '../services/integrationService';
import { customerService } from '../services/customerService';
import { conversationService } from '../services/conversationService';

import { Organization, User, ActiveCustomer, UnpaidInvoice, AgentInvoiceAnalysis } from '../types/organizations';
import { OrganizationIntegration, Integration } from '../types/integrations';
import { useUser } from '../context/UserContext';
import { useAIAssistant } from '../context/AIAssistantContext';
import { TitleContext } from '../components/Topbar';
import ToastNotification from '../components/ToastNotification';
import WeeklyDunningOpStep from '../components/onboarding/WeeklyDunningOpStep';

// Import the new modular components
import {
  OrganizationHeader,
    OrganizationInfoCard,
    BaseCreditCard,
  PrimaryContactCard,
  OnboardingCard,
  OrganizationTabs,
  DashboardTab,
  EmployeesTab,
  IntegrationsTab,
  CustomerCategoriesTab,
  APArchetypesTab,
  GovernanceOverlaysTab,
  CustomersTab,
  CustomersPSETab,
  InvoicesTab,
  UserModal,
  IntegrationModal,
  IntegrationSelectionModal,
  WebhookDataModal,
  RevokeConfirmationModal,
  AnalysesModal,
  BaseCreditTermsModal,
  BaseCreditLimitModal
} from '../components/organization';


const OrganizationDetails_i2: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const { accessToken, setUserAndToken } = useUser();
  const { setCurrentOrganization } = useAIAssistant();
  const { setDynamicTitle } = useContext(TitleContext);

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [integrations, setIntegrations] = useState<OrganizationIntegration[]>([]);
  const [customers, setCustomers] = useState<ActiveCustomer[]>([]);
  const [customersPage, setCustomersPage] = useState(1);
  const [customersTotal, setCustomersTotal] = useState(0);
  const [customersLoadingMore, setCustomersLoadingMore] = useState(false);
  const [customersCategory, setCustomersCategory] = useState<string>('All');
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [customerCategories, setCustomerCategories] = useState<any[]>([]);
  const CUSTOMERS_PAGE_SIZE = 20;
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [integrationsLoading, setIntegrationsLoading] = useState(true);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [error, setError] = useState('');
  const [usersError, setUsersError] = useState('');
  const [integrationsError, setIntegrationsError] = useState('');
  const [customersError, setCustomersError] = useState('');

  // Unpaid Invoices state
  const [invoices, setInvoices] = useState<UnpaidInvoice[]>([]);
  const [invoicesPage, setInvoicesPage] = useState(1);
  const [invoicesTotal, setInvoicesTotal] = useState(0);
  const INVOICES_PAGE_SIZE = 20;
  const [invoicesLoadingMore, setInvoicesLoadingMore] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState('');
  const [showAnalysesModal, setShowAnalysesModal] = useState(false);
  const [selectedAnalyses, setSelectedAnalyses] = useState<{ type: string; analyses: AgentInvoiceAnalysis[] } | null>(null);

  // Onboarding status state
  const [onboardingStatus, setOnboardingStatus] = useState<any>(null);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [onboardingError, setOnboardingError] = useState('');

  const [activeTab, setActiveTab] = useState<'dashboard' | 'upcoming-dunning' | 'employees' | 'integrations' | 'customer-categories' | 'ap-archetypes' | 'governance-overlays' | 'customers' | 'customers-pse' | 'invoices'>(() => {
    // Restore active tab from localStorage
    const savedTab = localStorage.getItem(`org_details_active_tab_${organizationId}`);
    return (savedTab as 'dashboard' | 'upcoming-dunning' | 'employees' | 'integrations' | 'customer-categories' | 'ap-archetypes' | 'governance-overlays' | 'customers' | 'customers-pse' | 'invoices') || 'dashboard';
  });
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    if (organizationId) {
      localStorage.setItem(`org_details_active_tab_${organizationId}`, activeTab);
    }
  }, [activeTab, organizationId]);

  // Cache organization name for AI Assistant when organization data is loaded
  useEffect(() => {
    if (organization && organizationId) {
      setCurrentOrganization(organizationId, organization.legal_entity_name);
    }
  }, [organization, organizationId, setCurrentOrganization]);

  // Fetch customer categories when customers tab is activated
  useEffect(() => {
    if (activeTab === 'customers' && organizationId && availableCategories.length === 0) {
      fetchCustomerCategories();
    }
  }, [activeTab, organizationId, availableCategories.length]);


  // Modal states for adding/editing users
  const [showUserModal, setShowUserModal] = useState(false);
  const [userModalMode, setUserModalMode] = useState<'add' | 'edit'>('add');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Integration states
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [showIntegrationSelectionModal, setShowIntegrationSelectionModal] = useState(false);
  const [showWebhookDataModal, setShowWebhookDataModal] = useState(false);
  const [selectedWebhookData, setSelectedWebhookData] = useState<any>(null);
  const [integrationUrl, setIntegrationUrl] = useState('');
  const [integrationLoading, setIntegrationLoading] = useState(false);
  const [availableIntegrations, setAvailableIntegrations] = useState<Integration[]>([]);

  // Revoke confirmation modal state
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [integrationToRevoke, setIntegrationToRevoke] = useState<string | null>(null);

  // Base Credit Terms and Limit modal states
  const [showBaseCreditTermsModal, setShowBaseCreditTermsModal] = useState(false);
  const [showBaseCreditLimitModal, setShowBaseCreditLimitModal] = useState(false);





  // Fetch organization details
  const fetchOrganizationDetails = async () => {
    if (!organizationId) return;

    try {
      setLoading(true);
      const response = await organizationService.getOrganization(organizationId!, accessToken, setUserAndToken);
      setOrganization(response);
      setDynamicTitle(`${response.legal_entity_name}`);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch organization details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch users for this organization
  const fetchOrganizationUsers = async () => {
    if (!organizationId) return;

    try {
      setUsersLoading(true);
      const response = await userService.getOrganizationUsers(organizationId!, 1, 1000, accessToken, setUserAndToken);
      setUsers(response.users);
      setUsersError('');
    } catch (err: any) {
      setUsersError(err.message || 'Failed to fetch organization users');
    } finally {
      setUsersLoading(false);
    }
  };

  // Fetch integrations for this organization
  const fetchOrganizationIntegrations = async () => {
    if (!organizationId) return;

    try {
      setIntegrationsLoading(true);
      const response = await integrationService.getOrganizationIntegrations(organizationId!, accessToken!, setUserAndToken);
      setIntegrations(response);
      setIntegrationsError('');
    } catch (err: any) {
      setIntegrationsError(err.message || 'Failed to fetch organization integrations');
    } finally {
      setIntegrationsLoading(false);
    }
  };

  // Fetch all customers for this organization
  const fetchAllCustomers = async (page: number = 1, category: string = customersCategory) => {
    if (!organizationId) return;

    try {
      if (page === 1) {
        setCustomersLoading(true);
      } else {
        setCustomersLoadingMore(true);
      }
      const response = await customerService.getAllCustomers(organizationId!, accessToken!, setUserAndToken, page, CUSTOMERS_PAGE_SIZE, category);
      // For Load More pattern, we need to handle pagination differently
      // Since we're now loading all customers at once, we'll simulate the Load More behavior
      if (page === 1) {
        setCustomers(response.customers || []);
      } else {
        // This won't be used since we load all at once, but keeping for compatibility
        setCustomers(prev => [...prev, ...(response.customers || [])]);
      }
      setCustomersTotal(response.total_count || 0);
      setCustomersPage(page);
      setCustomersError('');
    } catch (err: any) {
      setCustomersError(err.message || 'Failed to fetch customers');
    } finally {
      if (page === 1) {
        setCustomersLoading(false);
      } else {
        setCustomersLoadingMore(false);
      }
    }
  };

  // Fetch customers with rubrics data for dunning operations
  const [dunningCustomers, setDunningCustomers] = useState<any[]>([]);
  const [dunningCustomersLoading, setDunningCustomersLoading] = useState(false);
  const [dunningCustomersError, setDunningCustomersError] = useState('');

  const fetchDunningCustomers = async () => {
    if (!organizationId) return;

    try {
      setDunningCustomersLoading(true);
      const response = await customerService.getAllCustomersForPSE(organizationId!, accessToken!, setUserAndToken);
      setDunningCustomers(response.customers || []);
      setDunningCustomersError('');
    } catch (err: any) {
      setDunningCustomersError(err.message || 'Failed to fetch dunning customers');
    } finally {
      setDunningCustomersLoading(false);
    }
  };

  const handleRefreshCustomers = () => {
    fetchAllCustomers();
  };

  // Handle note count changes for customers
  const handleCustomerNoteCountChange = (customerId: string, delta: number) => {
    // Update main customers state
    setCustomers(prev => prev.map(customer => 
      customer.id === customerId 
        ? { ...customer, note_count: Math.max(0, (customer.note_count || 0) + delta) }
        : customer
    ));
    
    // Update dunning customers state
    setDunningCustomers(prev => prev.map(customer => 
      customer.id === customerId 
        ? { ...customer, note_count: Math.max(0, (customer.note_count || 0) + delta) }
        : customer
    ));
  };

  const handleCategoryChange = (category: string) => {
    setCustomersCategory(category);
    // Reset pagination and fetch customers with new category
    setCustomersPage(1);
    setCustomersTotal(0);
    fetchAllCustomers(1, category);
  };

  // Fetch available customer categories (just names for filtering)
  const fetchCustomerCategories = async () => {
    if (!organizationId) return;

    try {
      setCategoriesLoading(true);
      const response = await customerService.getCustomerCategories(organizationId, accessToken, setUserAndToken);
      setAvailableCategories(response.categories);
    } catch (err: any) {
      console.error('Error fetching customer categories:', err);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Fetch full customer categories data (with entries for dunning ladder)
  const fetchFullCustomerCategories = async () => {
    if (!organizationId) return;

    try {
      const categories = await organizationService.getOrganizationCustomerCategories(organizationId, accessToken, setUserAndToken);
      setCustomerCategories(categories);
    } catch (err: any) {
      console.error('Error fetching full customer categories:', err);
    }
  };

  // Fetch unpaid invoices for this organization
  const fetchUnpaidInvoices = async (page: number = 1) => {
    if (!organizationId) return;

    try {
      if (page === 1) {
        setInvoicesLoading(true);
      } else {
        setInvoicesLoadingMore(true);
      }
      const response = await customerService.getUnpaidInvoices(organizationId!, accessToken!, setUserAndToken, page, INVOICES_PAGE_SIZE);
      setInvoices(prev => page === 1 ? response.invoices : [...prev, ...response.invoices]);
      setInvoicesTotal(response.total_count || 0);
      setInvoicesPage(page);
      setInvoicesError('');
    } catch (err: any) {
      setInvoicesError(err.message || 'Failed to fetch unpaid invoices');
    } finally {
      if (page === 1) {
        setInvoicesLoading(false);
      } else {
        setInvoicesLoadingMore(false);
      }
    }
  };

  // Fetch available integrations
  const fetchAvailableIntegrations = async () => {
    try {
      const response = await integrationService.getAvailableIntegrations(accessToken!, setUserAndToken);
      setAvailableIntegrations(response);
    } catch (err: any) {
      console.error('Failed to fetch available integrations:', err);
    }
  };

  // Fetch onboarding status
  const fetchOnboardingStatus = async () => {
    if (!organizationId) return;

    try {
      setOnboardingLoading(true);
      const response = await organizationService.getOnboardingStatus(organizationId!, accessToken!, setUserAndToken);
      setOnboardingStatus(response);
      setOnboardingError('');
    } catch (err: any) {
      setOnboardingError(err.message || 'Failed to fetch onboarding status');
    } finally {
      setOnboardingLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) {
      fetchOrganizationDetails();
      fetchOrganizationUsers();
      fetchOrganizationIntegrations();
      fetchAllCustomers(1);
      fetchAvailableIntegrations();
      fetchOnboardingStatus();
      fetchFullCustomerCategories();
    }

    // Cleanup: reset title when component unmounts
    return () => {
      setDynamicTitle(null);
    };
  }, [organizationId]);

  // Fetch dunning customers when the upcoming-dunning tab is activated
  useEffect(() => {
    if (organizationId && activeTab === 'upcoming-dunning' && dunningCustomers.length === 0) {
      fetchDunningCustomers();
    }
  }, [activeTab, organizationId]);

  // Defer loading unpaid invoices until the Invoices tab is active
  useEffect(() => {
    if (organizationId && activeTab === 'invoices' && !invoicesLoading && invoices.length === 0) {
      fetchUnpaidInvoices(1);
    }
  }, [activeTab, organizationId]);

  const canLoadMoreCustomers = customers.length < customersTotal;
  const loadMoreCustomers = () => {
    if (!customersLoading && canLoadMoreCustomers) {
      fetchAllCustomers(customersPage + 1);
    }
  };

  const canLoadMoreInvoices = invoices.length < invoicesTotal;
  const loadMoreInvoices = () => {
    if (!invoicesLoading && canLoadMoreInvoices) {
      fetchUnpaidInvoices(invoicesPage + 1);
    }
  };

  // Handle user modal submit
  const handleUserModalSubmit = async (formData: any) => {
    if (!organizationId) return;

    try {
      if (userModalMode === 'add') {
        const newUser = await userService.createUser({
          ...formData,
          organization_id: organizationId
        }, accessToken, setUserAndToken);
        setUsers(prev => [...prev, newUser]);
      } else if (userModalMode === 'edit' && selectedUser) {
        const updatedUser = await userService.updateUser(selectedUser.id, formData, accessToken, setUserAndToken);
        setUsers(prev => prev.map(u => u.id === updatedUser.id ? updatedUser : u));
        setSelectedUser(null);
      }
    } catch (err: any) {
      throw err; // Let the modal handle the error display
    }
  };

  // Handle delete user
  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;

    try {
      await userService.deleteUser(userId, accessToken, setUserAndToken);
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err: any) {
      setError(err.message || 'Failed to delete the employee');
    }
  };

  // Set user as primary contact
  const handleSetPrimaryContact = async (userId: string) => {
    if (!organizationId) return;

    try {
      await userService.setPrimaryContact(organizationId, userId, accessToken, setUserAndToken);
      // Refresh users to get updated primary contact status
      fetchOrganizationUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to set primary contact');
    }
  };

  // Handle integration selection
  const handleAddIntegration = () => {
    setShowIntegrationSelectionModal(true);
  };

  // Handle specific integration creation
  const handleCreateIntegration = async (integration: Integration) => {
    if (!organizationId) return;

    try {
      setIntegrationLoading(true);


      if (integration.name === 'quickbooks-online') {
        // Show toast for QuickBooks Online
        setToastMsg('The integration for QuickBooks Online is still under development.');
        setShowIntegrationSelectionModal(false);
        return;
      }

      // Create organization integration
      
      const orgIntegration = await integrationService.createOrganizationIntegration(
        organizationId!,
        integration.id,
        integration.name,
        accessToken!,
        setUserAndToken
      );
      

      // Check if we have a valid authorization link
      if (orgIntegration.link_to_authorize) {
        setIntegrationUrl(orgIntegration.link_to_authorize);
        setShowIntegrationModal(true);
        setShowIntegrationSelectionModal(false);
        // Refresh integrations list
        fetchOrganizationIntegrations();
      } else {
        // No authorization link - this should not happen with proper backend validation
        console.error('❌ No link_to_authorize in response:', orgIntegration);
        throw new Error('Failed to generate integration link. Please try again or contact support.');
      }
    } catch (err: any) {
      console.error('❌ Integration creation error:', err);
      console.error('❌ Error details:', {
        message: err.message,
        status: err.status,
        response: err.response,
        stack: err.stack
      });
      
      // Re-throw the error so the modal can handle it
      throw err;
    } finally {
      setIntegrationLoading(false);
    }
  };

  // Handle revoke integration
  const handleRevokeIntegration = (integrationId: string) => {
    setIntegrationToRevoke(integrationId);
    setShowRevokeModal(true);
  };

  // Confirm revoke integration
  const confirmRevokeIntegration = async () => {
    if (!organizationId || !integrationToRevoke) return;

    try {
      await integrationService.revokeOrganizationIntegration(organizationId!, integrationToRevoke, accessToken!, setUserAndToken);
      // Refresh integrations list
      fetchOrganizationIntegrations();
      setToastMsg('Integration revoked successfully');
      setShowRevokeModal(false);
      setIntegrationToRevoke(null);
    } catch (err: any) {
      setToastMsg(err.message || 'Failed to revoke integration');
      setShowRevokeModal(false);
      setIntegrationToRevoke(null);
    }
  };

  // Handle recreate expired integration
  const handleRecreateIntegration = async (integrationId: string) => {
    if (!organizationId) return;

    try {
      const updatedIntegration = await integrationService.recreateExpiredIntegration(organizationId!, integrationId, accessToken!, setUserAndToken);

      // Show the integration modal with the new link
      if (updatedIntegration.link_to_authorize) {
        setIntegrationUrl(updatedIntegration.link_to_authorize);
        setShowIntegrationModal(true);
      }

      // Refresh integrations list
      fetchOrganizationIntegrations();
    } catch (err: any) {
      setError(err.message || 'Failed to recreate expired integration');
    }
  };

  // Copy integration URL to clipboard
  const copyIntegrationUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setToastMsg('The link is copied.');
    } catch (err) {
      console.error('Failed to copy URL:', err);
      setToastMsg('Failed to copy link to clipboard');
    }
  };

  // Handle copy button click
  const handleCopyClick = (url: string) => {
    copyIntegrationUrl(url);
  };

  // Handle view webhook data
  const handleViewWebhookData = (data: any) => {
    setSelectedWebhookData(data);
    setShowWebhookDataModal(true);
  };

  // Handle navigate to integration
  const handleNavigateToIntegration = (integrationId: string) => {
    navigate(`/integration/${integrationId}`);
  };

  // Handle view analyses
  const handleViewAnalyses = (type: string, analyses: AgentInvoiceAnalysis[]) => {
    setSelectedAnalyses({ type, analyses });
    setShowAnalysesModal(true);
  };

  // Handle authorize Xero
  const handleAuthorizeXero = async () => {
    // Check if there's a revoked integration that we can re-create
    if (onboardingStatus?.xero_authorization?.integration_id && 
        onboardingStatus.xero_authorization.authorization_status === 'revoked') {
      try {
        setIntegrationLoading(true);
        setToastMsg('Re-creating Xero integration...');

        const updatedIntegration = await integrationService.recreateExpiredIntegration(
          organizationId!,
          onboardingStatus.xero_authorization.integration_id,
          accessToken!,
          setUserAndToken
        );

        if (updatedIntegration.link_to_authorize) {
          setToastMsg('Opening Xero re-authorization...');
          window.open(updatedIntegration.link_to_authorize, '_blank');
          // Refresh onboarding status
          fetchOnboardingStatus();
        } else {
          throw new Error('Failed to generate Xero re-authorization link');
        }
      } catch (err: any) {
        console.error('Xero re-authorization error:', err);
        setToastMsg(err.message || 'Failed to re-authorize Xero');
      } finally {
        setIntegrationLoading(false);
      }
    } else {
      // Navigate to integrations tab and trigger Xero integration
      setActiveTab('integrations');
      // The integration selection modal will be handled by the IntegrationsTab
    }
  };

  // Handle view onboarding details
  const handleViewOnboardingDetails = () => {
    navigate(`/organizations/${organizationId}/onboarding`);
  };

  // Handle starting a conversation with primary contact
  const handleStartConversation = async (conversationId: string) => {
    try {
      // Navigate to WhatsApp manager with the specific conversation selected
      navigate(`/whatsapp-manager?conversation=${conversationId}`);
    } catch (err: any) {
      setToastMsg(err.message || 'Failed to start conversation');
    }
  };

  // Handle base credit terms modal submit
  const handleBaseCreditTermsSubmit = async (formData: { base_credit_terms: string; base_credit_terms_desc: string }) => {
    if (!organizationId || !organization) return;

    try {
      const updatedOrganization = await organizationService.updateOrganization(
        organizationId,
        {
          base_credit_terms: formData.base_credit_terms || null,
          base_credit_terms_desc: formData.base_credit_terms_desc || null
        },
        accessToken,
        setUserAndToken
      );
      setOrganization(updatedOrganization);
      setToastMsg('Base credit terms updated successfully');
      setShowBaseCreditTermsModal(false);
    } catch (err: any) {
      throw err; // Let the modal handle the error display
    }
  };

  // Handle base credit limit modal submit
  const handleBaseCreditLimitSubmit = async (formData: { base_credit_limit: string; base_credit_limit_desc: string }) => {
    if (!organizationId || !organization) return;

    try {
      const updatedOrganization = await organizationService.updateOrganization(
        organizationId,
        {
          base_credit_limit: formData.base_credit_limit ? parseFloat(formData.base_credit_limit) : null,
          base_credit_limit_desc: formData.base_credit_limit_desc || null
        },
        accessToken,
        setUserAndToken
      );
      setOrganization(updatedOrganization);
      setToastMsg('Base credit limit updated successfully');
      setShowBaseCreditLimitModal(false);
    } catch (err: any) {
      throw err; // Let the modal handle the error display
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading organization details...</span>
      </div>
    );
  }

  if (error || !organization) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error || 'Organization not found'}
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full max-w-[100vw] box-border px-4 overflow-x-hidden">
      {/* Header */}
      <OrganizationHeader onBack={() => navigate('/organizations')} />

      {/* Organization Information and Primary Contact + Onboarding */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 w-full max-w-full overflow-hidden">
        <div className="space-y-8 min-w-0">
          <OrganizationInfoCard organization={organization} />
          <BaseCreditCard
            organization={organization}
            onEditBaseCreditTerms={() => setShowBaseCreditTermsModal(true)}
            onEditBaseCreditLimit={() => setShowBaseCreditLimitModal(true)}
          />
        </div>
        <div className="space-y-8 min-w-0">
          <PrimaryContactCard 
            primaryContact={organization.primary_contact_user || null}
            organizationId={organizationId || ''}
            onStartConversation={handleStartConversation}
            accessToken={accessToken || ''}
            setUserAndToken={setUserAndToken}
          />
          <OnboardingCard 
            onboardingStatus={onboardingStatus}
            loading={onboardingLoading}
            error={onboardingError}
            onAuthorizeXero={handleAuthorizeXero}
            onViewDetails={handleViewOnboardingDetails}
            organizationId={organizationId || ''}
          />
        </div>
      </div>

      {/* Tabs and Content Section */}
      <div className="bg-white rounded-lg shadow w-full max-w-[100vw] overflow-x-auto">
        <OrganizationTabs activeTab={activeTab} onTabChange={setActiveTab} />
        
        {/* Tab Content */}
        {activeTab === 'dashboard' && (
          <DashboardTab organization={organization} />
        )}

        {activeTab === 'upcoming-dunning' && (
          <>
            {dunningCustomersLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading dunning customers...</span>
              </div>
            ) : dunningCustomersError ? (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {dunningCustomersError}
              </div>
            ) : (
              <WeeklyDunningOpStep
                customers={dunningCustomers}
                organizationId={organizationId || ''}
                customerCategories={customerCategories}
                organization={organization}
                onCustomerNoteCountChange={handleCustomerNoteCountChange}
              />
            )}
          </>
        )}

        {activeTab === 'employees' && (
          <EmployeesTab
            users={users}
            loading={usersLoading}
            error={usersError}
            onAddUser={() => {
              setUserModalMode('add');
              setShowUserModal(true);
            }}
            onEditUser={(user) => {
              setSelectedUser(user);
              setUserModalMode('edit');
              setShowUserModal(true);
            }}
            onDeleteUser={handleDeleteUser}
            onSetPrimaryContact={handleSetPrimaryContact}
          />
        )}

        {activeTab === 'integrations' && (
          <IntegrationsTab
            integrations={integrations}
            loading={integrationsLoading}
            error={integrationsError}
            integrationLoading={integrationLoading}
            onAddIntegration={handleAddIntegration}
            onRevokeIntegration={handleRevokeIntegration}
            onRecreateIntegration={handleRecreateIntegration}
            onCopyLink={handleCopyClick}
            onViewWebhookData={handleViewWebhookData}
            onNavigateToIntegration={handleNavigateToIntegration}
          />
        )}

        {activeTab === 'customer-categories' && (
          <CustomerCategoriesTab
            organizationId={organizationId || ''}
            accessToken={accessToken}
            setUserAndToken={setUserAndToken}
          />
        )}

        {activeTab === 'ap-archetypes' && (
          <APArchetypesTab
            organizationId={organizationId || ''}
            accessToken={accessToken}
            setUserAndToken={setUserAndToken}
          />
        )}

        {activeTab === 'governance-overlays' && (
          <GovernanceOverlaysTab
            organizationId={organizationId || ''}
            accessToken={accessToken}
            setUserAndToken={setUserAndToken}
          />
        )}

        {activeTab === 'customers' && (
          <CustomersTab
            organizationId={organizationId || ''}
            accessToken={accessToken}
            setUserAndToken={setUserAndToken}
            onCustomerNoteCountChange={handleCustomerNoteCountChange}
          />
        )}

        {activeTab === 'customers-pse' && (
          <CustomersPSETab
            organizationId={organizationId || ''}
            accessToken={accessToken}
            setUserAndToken={setUserAndToken}
            onCustomerNoteCountChange={handleCustomerNoteCountChange}
          />
        )}

        {activeTab === 'invoices' && (
          <InvoicesTab
            invoices={invoices}
            loading={invoicesLoading}
            loadingMore={invoicesLoadingMore}
            error={invoicesError}
            organizationId={organizationId || ''}
            onViewAnalyses={handleViewAnalyses}
            onRefreshInvoices={() => fetchUnpaidInvoices(1)}
            // Pagination controls
            onLoadMore={loadMoreInvoices}
            canLoadMore={canLoadMoreInvoices}
            totalCount={invoicesTotal}
          />
        )}
      </div>

      {/* Toast Notification */}
      <ToastNotification toastMsg={toastMsg} setToastMsg={setToastMsg} />

      {/* Modal Components */}
      <UserModal
        isOpen={showUserModal}
        mode={userModalMode}
        user={selectedUser}
        organizationId={organizationId || ''}
        onClose={() => {
          setShowUserModal(false);
          setSelectedUser(null);
        }}
        onSubmit={handleUserModalSubmit}
      />

      <IntegrationModal
        isOpen={showIntegrationModal}
        integrationUrl={integrationUrl}
        onClose={() => setShowIntegrationModal(false)}
        onCopyUrl={copyIntegrationUrl}
      />

      <IntegrationSelectionModal
        isOpen={showIntegrationSelectionModal}
        availableIntegrations={availableIntegrations}
        onClose={() => setShowIntegrationSelectionModal(false)}
        onSelectIntegration={handleCreateIntegration}
        isLoading={integrationLoading}
      />

      <WebhookDataModal
        isOpen={showWebhookDataModal}
        webhookData={selectedWebhookData}
        onClose={() => setShowWebhookDataModal(false)}
      />

      <RevokeConfirmationModal
        isOpen={showRevokeModal}
        integrationName={integrations.find(i => i.id === integrationToRevoke)?.integration?.name}
        onClose={() => setShowRevokeModal(false)}
        onConfirm={confirmRevokeIntegration}
        isLoading={integrationLoading}
      />

      <AnalysesModal
        isOpen={showAnalysesModal}
        analyses={selectedAnalyses}
        onClose={() => setShowAnalysesModal(false)}
      />

      <BaseCreditTermsModal
        isOpen={showBaseCreditTermsModal}
        organization={organization}
        onClose={() => setShowBaseCreditTermsModal(false)}
        onSubmit={handleBaseCreditTermsSubmit}
      />

      <BaseCreditLimitModal
        isOpen={showBaseCreditLimitModal}
        organization={organization}
        onClose={() => setShowBaseCreditLimitModal(false)}
        onSubmit={handleBaseCreditLimitSubmit}
      />

    </div>
  );
};

export default OrganizationDetails_i2;
